import React, { useState } from 'react';
import { Mail, Eye, EyeOff, Check } from 'lucide-react';

export default function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [productUpdatesOptIn, setProductUpdatesOptIn] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resendStatus, setResendStatus] = useState({ loading: false, message: '', error: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (!termsAccepted) {
      setError('You must accept the terms and privacy policy to continue.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
          source: 'direct',
          productUpdatesOptIn,
          marketingOptIn,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Meta pixel conversion (index.html'de init ediliyor)
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', { content_name: 'signup', status: true });
      }

      // Email verification flow — show "check your email" screen, do NOT auto-login
      setSubmittedEmail(formData.email);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) return;
    setResendStatus({ loading: true, message: '', error: '' });
    try {
      const resp = await fetch('https://api.glowytics.com/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: submittedEmail }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setResendStatus({ loading: false, message: '', error: data.error || 'Could not resend email.' });
        return;
      }
      setResendStatus({ loading: false, message: 'Verification email sent. Check your inbox.', error: '' });
    } catch (e) {
      setResendStatus({ loading: false, message: '', error: e.message || 'Network error' });
    }
  };

  const BRAND = '#436AEB';

  const inputCls =
    'mt-1.5 block w-full h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 ' +
    'focus:outline-none focus:border-[#436AEB] focus:ring-4 focus:ring-[#436AEB]/15 transition';

  const checkboxCls = 'mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#436AEB] focus:ring-[#436AEB]';

  // Örnek panel kartı (gösterim amaçlı, gerçek marka verisi değil)
  const platforms = [
    { name: 'Google Ads', logo: '/googleads-logo.svg', spend: '₺48.2K', roas: '5.4x', up: true },
    { name: 'Meta Ads', logo: '/deck/mecra/meta.svg', spend: '₺36.9K', roas: '4.1x', up: true },
    { name: 'GA4', logo: '/GA4logoicon.svg', spend: '—', roas: '2.8%', up: false, metricLabel: 'CVR' },
  ];
  const connectors = [
    { name: 'Google Ads', logo: '/googleads-logo.svg' },
    { name: 'Meta', logo: '/deck/mecra/meta.svg' },
    { name: 'GA4', logo: '/GA4logoicon.svg' },
    { name: 'Shopify', logo: '/shopify_glyph.svg' },
    { name: 'ikas', logo: '/ikaslogoicon.png' },
    { name: 'Pinterest', logo: '/pinterest-logo.svg' },
  ];
  const benefits = [
    'All ad platforms and sales in one dashboard',
    'Daily reports and alerts by email',
    '14-day free trial, no credit card required',
  ];

  return (
    <div className="min-h-screen bg-white flex">
      {/* Form */}
      <div className="w-full lg:w-[52%] flex flex-col px-6 sm:px-12 py-8">
        <a href="https://glowytics.com" className="inline-block">
          <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-8" />
        </a>

        <div className="flex-1 flex items-start lg:items-center justify-center pt-8 pb-10 sm:py-10">
          <div className="w-full max-w-[400px]">
            {submittedEmail ? (
              <div>
                <div className="w-12 h-12 rounded-xl bg-[#436AEB]/10 flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6" style={{ color: BRAND }} />
                </div>
                <h1 className="text-[28px] font-semibold tracking-tight text-slate-900">Check your email</h1>
                <p className="mt-3 text-slate-600">
                  We sent a verification link to{' '}
                  <span className="font-medium text-slate-900 break-all">{submittedEmail}</span>.
                  Click it to activate your account. The link expires in 24 hours.
                </p>

                {resendStatus.message && (
                  <div className="mt-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                    {resendStatus.message}
                  </div>
                )}
                {resendStatus.error && (
                  <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {resendStatus.error}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendStatus.loading}
                  className="mt-8 w-full h-11 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
                >
                  {resendStatus.loading ? 'Sending…' : 'Resend verification email'}
                </button>
                <p className="mt-4 text-sm text-slate-500">
                  Already verified?{' '}
                  <a href="/login" className="font-medium hover:underline" style={{ color: BRAND }}>Sign in</a>
                </p>
              </div>
            ) : (
              <>
                <h1 className="text-[28px] font-semibold tracking-tight text-slate-900">Create your account</h1>
                <p className="mt-2 text-slate-600">
                  Start your 14-day free trial. No credit card required.
                </p>

                <form className="mt-8" onSubmit={handleSubmit}>
                  {error && (
                    <div className="mb-5 rounded-lg bg-red-50 px-3.5 py-3 border border-red-200">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-slate-700">Full name</label>
                      <input id="fullName" name="fullName" type="text" autoComplete="name" required
                        value={formData.fullName} onChange={handleChange} className={inputCls} />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700">Work email</label>
                      <input id="email" name="email" type="email" autoComplete="email" required
                        value={formData.email} onChange={handleChange} className={inputCls}
                        placeholder="name@company.com" />
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-slate-700">Password</label>
                      <div className="relative">
                        <input id="password" name="password" type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password" required minLength={6}
                          value={formData.password} onChange={handleChange}
                          className={inputCls + ' pr-11'} placeholder="At least 6 characters" />
                        <button type="button" onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 mt-[3px] w-10 h-10 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3 text-sm text-slate-600">
                    <label htmlFor="terms" className="flex items-start gap-3 cursor-pointer">
                      <input id="terms" name="terms" type="checkbox" checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)} className={checkboxCls} />
                      <span>
                        I agree to the{' '}
                        <a href="/terms" target="_blank" rel="noreferrer" className="font-medium hover:underline" style={{ color: BRAND }}>Terms of Service</a>{' '}
                        and{' '}
                        <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium hover:underline" style={{ color: BRAND }}>Privacy Policy</a>.
                      </span>
                    </label>
                    <label htmlFor="productUpdates" className="flex items-start gap-3 cursor-pointer">
                      <input id="productUpdates" name="productUpdates" type="checkbox" checked={productUpdatesOptIn}
                        onChange={(e) => setProductUpdatesOptIn(e.target.checked)} className={checkboxCls} />
                      <span>Send me product updates, weekly reports, and tips to get the most out of Glowytics.</span>
                    </label>
                    <label htmlFor="marketing" className="flex items-start gap-3 cursor-pointer">
                      <input id="marketing" name="marketing" type="checkbox" checked={marketingOptIn}
                        onChange={(e) => setMarketingOptIn(e.target.checked)} className={checkboxCls} />
                      <span>I'd like to receive promotional emails about new features, offers, and events. You can unsubscribe at any time.</span>
                    </label>
                  </div>

                  <button type="submit" disabled={loading}
                    className="mt-7 w-full h-11 rounded-lg text-sm font-semibold text-white bg-[#436AEB] hover:bg-[#3557D1] active:bg-[#2D4BB8] focus:outline-none focus:ring-4 focus:ring-[#436AEB]/25 transition disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? 'Creating account…' : 'Create free account'}
                  </button>

                  <p className="mt-6 text-sm text-slate-600 text-center">
                    Already have an account?{' '}
                    <a href="/login" className="font-medium hover:underline" style={{ color: BRAND }}>Log in</a>
                  </p>
                </form>
              </>
            )}

            {/* Mobil/tablet: sağ panel gizli, kısa ürün özeti formun altında */}
            {!submittedEmail && (
              <div className="lg:hidden mt-10 pt-8 border-t border-slate-200">
                <ul className="space-y-2.5">
                  {benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <span className="w-5 h-5 shrink-0 rounded-full bg-[#436AEB]/10 flex items-center justify-center">
                        <Check className="w-3 h-3" style={{ color: BRAND }} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs font-medium text-slate-500">Connects with</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {connectors.map((c) => (
                    <span key={c.name} title={c.name}
                      className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      <img src={c.logo} alt={c.name} className="w-5 h-5 object-contain" loading="lazy" />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-400">© {new Date().getFullYear()} Glowytics</p>
      </div>

      {/* Ürün önizlemesi */}
      <div className="hidden lg:flex w-[48%] bg-[#F4F6FB] border-l border-slate-200 flex-col justify-center px-12 xl:px-16 py-12">
        <div className="max-w-[520px] w-full mx-auto">
          <p className="text-sm font-medium" style={{ color: BRAND }}>Marketing analytics for e-commerce</p>
          <h2 className="mt-2 text-[30px] leading-[1.2] font-semibold tracking-tight text-slate-900">
            See every channel's spend and return in one place.
          </h2>

          {/* Örnek panel kartı */}
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(15,23,42,0.12)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <span className="text-sm font-semibold text-slate-800">Performance overview</span>
              <span className="text-xs text-slate-500 bg-slate-100 rounded-md px-2 py-1">Last 7 days</span>
            </div>
            <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
              {[['Spend', '₺85.1K'], ['Revenue', '₺412K'], ['ROAS', '4.8x']].map(([k, v]) => (
                <div key={k} className="px-5 py-4">
                  <div className="text-xs text-slate-500">{k}</div>
                  <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{v}</div>
                </div>
              ))}
            </div>
            <div className="px-5 pt-4">
              <svg viewBox="0 0 440 90" className="w-full h-[90px]" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="su-area" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={BRAND} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={BRAND} stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[22, 45, 68].map((y) => <line key={y} x1="0" x2="440" y1={y} y2={y} stroke="#EEF1F6" strokeWidth="1" />)}
                <path d="M0,70 L63,62 L126,66 L189,48 L252,52 L315,34 L378,38 L440,20 L440,90 L0,90 Z" fill="url(#su-area)" />
                <path d="M0,70 L63,62 L126,66 L189,48 L252,52 L315,34 L378,38 L440,20" fill="none" stroke={BRAND} strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />
                <path d="M0,78 L63,74 L126,77 L189,70 L252,71 L315,64 L378,66 L440,58" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 4" />
              </svg>
            </div>
            <div className="px-5 pb-2 pt-2">
              {platforms.map((p) => (
                <div key={p.name} className="flex items-center gap-3 py-2.5 border-t border-slate-100 first:border-t-0">
                  <img src={p.logo} alt="" className="w-5 h-5 object-contain" />
                  <span className="text-sm text-slate-700 flex-1">{p.name}</span>
                  <span className="text-sm text-slate-500 tabular-nums w-16 text-right">{p.spend}</span>
                  <span className="text-sm font-medium text-slate-900 tabular-nums w-20 text-right">
                    {p.metricLabel ? <span className="text-xs text-slate-400 font-normal mr-1">{p.metricLabel}</span> : null}
                    {p.roas}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Sample data</p>

          <ul className="mt-6 space-y-2.5">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                <span className="w-5 h-5 rounded-full bg-[#436AEB]/10 flex items-center justify-center">
                  <Check className="w-3 h-3" style={{ color: BRAND }} strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-xs font-medium text-slate-500">Connects with</p>
            <div className="mt-3 flex items-center gap-2.5 flex-wrap">
              {connectors.map((c) => (
                <span key={c.name} title={c.name}
                  className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                  <img src={c.logo} alt={c.name} className="w-5 h-5 object-contain" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
